import React from 'react';
import { DeploymentUnitOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Search } from '../../../types';
import SearchPanel from './SearchPanel';
import css from './SearchPack.module.scss';

interface SearchPackProps {
  searches: Search[];
  onUpdate: () => void;
  onOpenProgress?: (search: Search) => void;
}

/**
 * A "pack" of searches created together via Multi-Agent Search (shared groupId).
 * Display-only grouping: each member is a fully independent SearchPanel
 * (pause/delete/view all still per-search) — the header just gives the pack an
 * identity and an at-a-glance roll-up.
 */
const SearchPack: React.FC<SearchPackProps> = ({ searches, onUpdate, onOpenProgress }) => {
  const agentCount = searches.length;
  const totalOpportunities = searches.reduce(
    (sum, s) => sum + (s.stats?.totalOpportunities ?? 0),
    0
  );

  return (
    <section className={css.pack} aria-label={`Multi-Agent Search · ${agentCount} agents`}>
      <header className={css.header}>
        <div className={css.mark} aria-hidden="true">
          <DeploymentUnitOutlined />
        </div>
        <div className={css.titles}>
          <h2 className={css.title}>Multi-Agent Search</h2>
          <p className={css.subtitle}>{agentCount} agents widening your coverage</p>
        </div>
        <div className={css.summary}>
          <ThunderboltOutlined />
          <span>
            <b>{totalOpportunities.toLocaleString()}</b> opportunities
          </span>
        </div>
      </header>

      <div className={css.body}>
        {searches.map((search) => (
          <div className={css.member} key={search.id}>
            <SearchPanel
              search={search}
              onUpdate={onUpdate}
              onOpenProgress={onOpenProgress}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export default SearchPack;
